import { Instagram, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TestimonialCard from "@/components/TestimonialCard";
import { useNavigate, useLocation } from 'react-router-dom'
import { useReviews } from "@/hooks/useReviews";
import { useExperiments } from "@/hooks/useExperiments";
import { useBulkExperimentResults } from "@/hooks/useBulkExperimentResults";
import MiniGlucoseChart from "@/components/MiniGlucoseChart";
import EmptyState from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import VideoModal from "@/components/VideoModal";
const Tastermonials = () => {
  const { reviews, loading, error } = useReviews();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: experiments = [] } = useExperiments();

  // State for video modal
  const [videoModal, setVideoModal] = useState<{
    isOpen: boolean;
    videoPath: string;
    reviewerName: string;
    experimentName: string;
  }>({
    isOpen: false,
    videoPath: '',
    reviewerName: '',
    experimentName: ''
  });

  // Map experiment names to IDs
  const experimentIdByName = new Map<string, string>(
    experiments.map((e: { name: string; id: string }) => [e.name, e.id])
  );

  // Unique experiment IDs that have reviews
  const reviewExperimentIds = Array.from(
    new Set(
      reviews
        .map((r) => experimentIdByName.get(r.experimentName))
        .filter((id): id is string => Boolean(id))
    )
  );

  // Fetch results for all relevant experiments
  const { resultsByExperimentId } = useBulkExperimentResults(reviewExperimentIds);

  const getImpactForReview = (review: { experimentName: string; userId: string }) => {
    const expId = experimentIdByName.get(review.experimentName);
    if (!expId) return undefined;
    const results = resultsByExperimentId[expId];
    const impact = results?.meal_impacts?.find(
      (mi) => mi.user_id === review.userId && Array.isArray(mi.glucose_samples) && mi.glucose_samples.length > 0
    );
    return impact;
  };

  // Filter reviews into sections
  const publicReviews = reviews.filter((review) => !review.isAnonymous);
  const anonymousReviews = reviews.filter((review) => review.isAnonymous);
  const socialPosts = reviews.filter((review) => review.socialMediaUrls.length > 0);
  const videoReviews = reviews.filter((review) => !review.allowVideoUse && review.videos.length > 0);
  const videoReviewCards = reviews.filter((review) => review.allowVideoUse && review.videos.length > 0);

  const handleReviewClick = (userId: string) => {
    navigate(`/dashboard/participants/${userId}`, { state: { from: location.pathname, fromLabel: 'tastermonials' } });
  };

  const getVideoUrl = async (videoPath: string): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'GET',
          endpoint: '/api/v2/storage/url',
          queryString: `path=${encodeURIComponent(`s3://tastermonialuservideos/${videoPath}`)}`
        }
      });

      if (error) {
        throw new Error(`Failed to get video URL: ${error.message}`);
      }

      // The API returns { url: string, expires_in: number }
      if (!data.data || !data.data.url) {
        throw new Error('Invalid response: missing URL');
      }

      return data.data.url;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  };

  const handleVideoPlay = (videoPath: string, reviewerName: string, experimentName: string) => {
    setVideoModal({
      isOpen: true,
      videoPath,
      reviewerName,
      experimentName
    });
  };

  const handleVideoModalClose = () => {
    setVideoModal({
      isOpen: false,
      videoPath: '',
      reviewerName: '',
      experimentName: ''
    });
  };

  const handleVideoDownload = async (videoPath: string) => {
    try {
      const videoUrl = await getVideoUrl(videoPath);
      // Create a temporary link to download the video
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download video:', err);
    }
  };


  return <div>
      <h1 className="text-3xl font-bold mb-6">Tastermonials</h1>
      <p className="text-muted-foreground mb-8 gap-2">Real user experiences and unfiltered insights from our community. Participants in our studies are rewarded for participating regardless of the outcome and reviews they leave, ensuring your brand get unfiltered and unbiased natural and personal reviews, backed by data and verified by Tastermonial.</p>

      {loading ? (
        <div className="text-center py-8">Loading reviews...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">
          Error loading reviews: {error}
        </div>
      ) : reviews.length === 0 ? (
        <EmptyState
          title="No reviews yet"
          description="Once participants complete their experiments and submit feedback, their reviews will appear here."
          imageUrl="/images/join-page/verified-user-testimonials.png"
        />
      ) : (
        <>
          {/* Social Media Posts Section */}
          {socialPosts.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold mb-6">Social Media Posts</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {socialPosts.map((review) => (
                  <div key={review.id}>
                    {review.socialMediaUrls.map((url, index) => (
                      <Card key={`${review.id}-${index}`} className="overflow-hidden relative mb-4">
                        {review.isInfluencer && (
                          <div className="absolute top-4 right-4 z-10">
                            <Badge variant="default" className="bg-glyco-purple text-white font-medium">
                              Verified Influencer
                            </Badge>
                          </div>
                        )}
                        <CardContent className="p-6">
                          <div
                            className="relative mb-4 aspect-[4/5] rounded-lg overflow-hidden group cursor-pointer"
                            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                          >
                            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                              <Instagram className="w-16 h-16 text-purple-500" />
                            </div>
                            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mb-4">
                            <Instagram className="h-5 w-5" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{review.name}</p>
                              </div>
                              <p className="text-sm text-muted-foreground">{review.experimentName}</p>
                            </div>
                          </div>
                          <p className="text-sm mb-4 line-clamp-4">{review.content}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video Review Cards Section */}
          {videoReviewCards.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold mb-6">Video Reviews</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 justify-items-start">
                {videoReviewCards.map((review) => (
                  <div key={review.id}>
                    {review.videos.map((video, index) => (
                      <Card key={`${review.id}-video-${index}`} className="overflow-hidden relative mb-4">
                        {review.isInfluencer && (
                          <div className="absolute top-4 right-4 z-10">
                            <Badge variant="default" className="bg-glyco-purple text-white font-medium">
                              Verified Influencer
                            </Badge>
                          </div>
                        )}
                        <CardContent className="p-6">
                          <div
                            className="relative mb-4 aspect-[3/4] rounded-lg overflow-hidden group cursor-pointer"
                            onClick={() => handleVideoPlay(video.path, review.name, review.experimentName)}
                          >
                            <img
                              src={video.thumbnailUrl}
                              alt={`Video thumbnail for ${review.name}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          </div>
                            <div className="flex items-center gap-3 mb-4">
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium">{review.name}</p>
                                </div>
                                <p className="text-sm text-muted-foreground">{review.experimentName}</p>
                              </div>
                            </div>
                            <p className="text-sm mb-4 line-clamp-4">{review.content}</p>
                            {review.allowVideoUse ? (
                              <button
                                onClick={() => handleVideoDownload(video.path)}
                                className="text-sm text-blue-600 hover:underline cursor-pointer"
                              >
                                Download Video →
                              </button>
                            ) : (
                              <div className="text-sm text-gray-400">
                                <span className="block">Download Video →</span>
                                <span className="text-xs text-amber-600">Not approved for marketing use</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Reviews Section */}
          {publicReviews.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold mb-6">Public Reviews</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {publicReviews.map((review) => {
                  const impact = getImpactForReview(review);
                  return (
                    <TestimonialCard
                      key={review.id}
                      name={review.name}
                      initials={review.initials}
                      testimonial={review.content}
                      rating={review.rating}
                      wouldRecommend={review.wouldRecommend}
                      submittedAt={review.submittedAt}
                      onClick={() => handleReviewClick(review.userId)}
                      videoUrl={review.videos.length > 0 ? review.videos[0].path : undefined}
                      footerChart={impact ? (
                        <MiniGlucoseChart
                          samples={impact.glucose_samples}
                          startGlucose={impact.start_glucose_level}
                          baselineSamples={impact.baseline_glucose_samples}
                          showDownload={false}
                        />
                      ) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Anonymous Reviews Section */}
          {anonymousReviews.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold mb-6">Anonymous Reviews</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {anonymousReviews.map((review) => {
                  const impact = getImpactForReview(review);
                  return (
                    <TestimonialCard
                      key={review.id}
                      name="Anonymous"
                      initials="A"
                      testimonial={review.content}
                      rating={review.rating}
                      wouldRecommend={review.wouldRecommend}
                      submittedAt={review.submittedAt}
                      onClick={() => handleReviewClick(review.userId)}
                      videoUrl={review.videos.length > 0 ? review.videos[0].path : undefined}
                      footerChart={impact ? (
                        <MiniGlucoseChart
                          samples={impact.glucose_samples}
                          startGlucose={impact.start_glucose_level}
                          baselineSamples={impact.baseline_glucose_samples}
                          showDownload={false}
                        />
                      ) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Video Reviews Section (Non-public videos) */}
          {videoReviews.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold mb-6">Video Reviews</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 justify-items-start">
                {videoReviews.map((review) => (
                  <div key={review.id}>
                    {review.videos.map((video, index) => (
                      <Card key={`${review.id}-video-${index}`} className="overflow-hidden relative mb-4">
                        {review.isInfluencer && (
                          <div className="absolute top-4 right-4 z-10">
                            <Badge variant="default" className="bg-glyco-purple text-white font-medium">
                              Verified Influencer
                            </Badge>
                          </div>
                        )}
                        <CardContent className="p-6">
                          <div
                            className="relative mb-4 aspect-[3/4] rounded-lg overflow-hidden group cursor-pointer"
                            onClick={() => handleVideoPlay(video.path, review.name, review.experimentName)}
                          >
                            <img
                              src={video.thumbnailUrl}
                              alt={`Video thumbnail for ${review.name}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{review.name}</p>
                              </div>
                              <p className="text-sm text-muted-foreground">{review.experimentName}</p>
                            </div>
                          </div>
                          <p className="text-sm mb-4 line-clamp-4">{review.content}</p>
                          {review.allowVideoUse ? (
                            <button
                              onClick={() => handleVideoDownload(video.path)}
                              className="text-sm text-blue-600 hover:underline cursor-pointer"
                            >
                              Download Video →
                            </button>
                          ) : (
                            <div className="text-sm text-gray-400">
                              <span className="block">Download Video →</span>
                              <span className="text-xs text-amber-600">Not approved for marketing use</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Video Modal */}
      <VideoModal
        isOpen={videoModal.isOpen}
        onClose={handleVideoModalClose}
        videoPath={videoModal.videoPath}
        reviewerName={videoModal.reviewerName}
        experimentName={videoModal.experimentName}
      />
    </div>;
};

export default Tastermonials;
